<template>
	<v-dialog
		:value="value"
		max-width="760"
		@input="handleDialogInput"
	>
		<v-card>
			<v-card-title class="primary white--text body-1">
				<v-icon small color="white" class="mr-2">mdi-face-recognition</v-icon>
				Add trained face
				<v-spacer></v-spacer>
				<v-btn icon x-small color="white" @click.stop="close">
					<v-icon small>mdi-close</v-icon>
				</v-btn>
			</v-card-title>

			<v-card-text class="pa-3">
				<TrainedFaceSourcePicker
					:value="selectedSource"
					:sources="sources"
					@input="selectedSource = $event"
				/>

				<v-alert v-if="sourceNotice" dense outlined type="info" class="mt-3">
					{{ sourceNotice }}
				</v-alert>

				<TrainedFaceCapturePanel
					v-if="selectedSource"
					class="mt-3"
					:source="selectedSource"
					:loading="loadingUpload"
					:label="uploadLabel"
					:registered-cameras="registeredCameras"
					:selected-registered-camera-id="selectedRegisteredCameraId"
					:selected-registered-camera="selectedRegisteredCamera"
					:loading-registered-cameras="loadingRegisteredCameras"
					:registered-stream-active="Boolean(registeredStreamSessionId)"
					:registered-stream-loading="registeredStreamLoading"
					:registered-stream-preview="registeredStreamPreview"
					:registered-stream-error="registeredCameraError"
					@upload-files="handleUploadFiles"
					@capture-file="handleCapturedFile"
					@select-registered-camera="selectRegisteredCamera"
					@start-registered-stream="startRegisteredCameraPreview"
					@capture-registered-frame="captureRegisteredCameraSnapshot"
					@stop-registered-stream="stopRegisteredCameraStream"
				/>

				<TrainedFaceAttemptReview
					v-if="attempts.length"
					class="mt-3"
					:attempts="attempts"
					:selected-id="selectedAttemptId"
					@select="selectedAttemptId = $event"
				/>

				<v-row dense class="mt-3">
					<v-col cols="12" md="6">
						<v-text-field
							v-model="info.name"
							dense
							outlined
							label="Name"
						></v-text-field>
					</v-col>
					<v-col cols="12" md="6">
						<v-text-field
							v-model="info.email"
							dense
							outlined
							label="Email"
						></v-text-field>
					</v-col>
				</v-row>

				<TrainedFaceListAssignment
					class="mt-2"
					:value="selectedListIds"
					:lists="faceLists"
					:loading="loadingFaceLists"
					:creating="creatingFaceList"
					:new-list-name="newListName"
					:error="listAssignmentError"
					:create-error="createListError"
					:pending-message="pendingListAssignmentMessage"
					:title="t('trained_face_list_assignment_title', 'Assign to face lists')"
					:select-label="t('trained_face_list_assignment_title', 'Assign to face lists')"
					:empty-text="t('trained_face_list_empty', 'No face lists yet. Create one here to continue.')"
					:create-label="t('trained_face_list_create_label', 'New list name')"
					:create-action="t('trained_face_list_create_action', 'Create list')"
					:create-cancel="t('cancel', 'Cancel')"
					:close-action="t('close', 'Close')"
					:retry-text="t('trained_face_list_assignment_retry', 'Retry assignment')"
					@input="selectedListIds = $event"
					@update:new-list-name="newListName = $event"
					@create="createFaceListInline"
					@retry-pending="retryPendingListAssignment"
				/>

				<v-alert v-if="messageErrorUpload.length" dense outlined type="warning" class="mt-2">
					{{ messageErrorUpload.join(', ') }}
				</v-alert>
			</v-card-text>

			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn x-small outlined color="error" @click.stop="close">Cancel</v-btn>
				<v-btn
					x-small
					color="primary"
					:loading="saving"
					:disabled="saveDisabled"
					@click="saveEnrollment"
				>
					Save
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
	import FaceService, { ACCESS_TERMINAL_ONLY_MESSAGE } from '@/services/face.service.js'
	import AccessControlService from '@/services/accessControl.service.js'
	import EventBus from '@/event-bus'
	import ListService, { normalizeListResponse as normalizeServiceListResponse } from '@/services/list.service.js'
	import { formatFaceEnrollmentError, formatRegisteredCameraError } from '@/services/cameraErrorFormatter.js'
	import TrainedFaceAttemptReview from './TrainedFaceAttemptReview.vue'
	import TrainedFaceCapturePanel from './TrainedFaceCapturePanel.vue'
	import TrainedFaceListAssignment from './TrainedFaceListAssignment.vue'
	import TrainedFaceSourcePicker from './TrainedFaceSourcePicker.vue'

	export default {
		name: 'TrainedFaceEnrollmentModal',
		components: {
			TrainedFaceAttemptReview,
			TrainedFaceCapturePanel,
			TrainedFaceListAssignment,
			TrainedFaceSourcePicker,
		},
		props: {
			value: {
				type: Boolean,
				default: false,
			},
			initialListIds: {
				type: Array,
				default: () => [],
			},
		},
		data: () => ({
			selectedSource: 'upload',
			attempts: [],
			selectedAttemptId: null,
			info: {
				type: 'user',
				name: '',
				email: '',
			},
			loadingUpload: false,
			loadingRegisteredCameras: false,
			registeredCameras: [],
			selectedRegisteredCameraId: null,
			registeredStreamSessionId: null,
			registeredStreamHeartbeatTimer: null,
			registeredStreamPreviewTimer: null,
			registeredStreamEventName: '',
			registeredStreamHandler: null,
			registeredStreamLatestFrame: null,
			registeredStreamLoading: false,
			registeredStreamPreview: '',
			registeredCameraError: '',
			faceLists: [],
			selectedListIds: [],
			loadingFaceLists: false,
			creatingFaceList: false,
			newListName: '',
			listAssignmentError: '',
			createListError: '',
			pendingListAssignment: null,
			saving: false,
			messageErrorUpload: [],
		}),
		computed: {
			sources(){
				return this.buildSources(this.isBrowserCameraAvailable())
			},
			sourceNotice(){
				if (this.selectedSource === 'registered-camera' && this.registeredCameraError) return this.registeredCameraError
				const source = this.sources.find(item => item.key === this.selectedSource)
				return source && source.disabled ? source.hint : ''
			},
			uploadLabel(){
				return this.$ml && this.$ml.get ? this.$ml.get('seleccion_files_to_train') : 'Select face images'
			},
			selectedAttempt(){
				return this.attempts.find(attempt => attempt.id === this.selectedAttemptId)
			},
			selectedRegisteredCamera(){
				return this.registeredCameras.find(camera => this.getCameraId(camera) === this.selectedRegisteredCameraId) || null
			},
			saveDisabled(){
				return !this.info.name || !this.selectedAttempt || !this.selectedAttempt.isValid || !this.selectedListIds.length || Boolean(this.messageErrorUpload.length)
			},
			pendingListAssignmentMessage(){
				return this.pendingListAssignment ? this.t('trained_face_list_assignment_pending', 'Face saved, but list assignment is still pending. Retry list assignment before considering it complete.') : ''
			},
		},
		watch: {
			selectedSource(source, previousSource){
				if (previousSource === 'registered-camera' && source !== 'registered-camera') this.stopRegisteredCameraStream()
				if (source === 'registered-camera') this.loadRegisteredCameras()
			},
			selectedRegisteredCameraId(cameraId, previousCameraId){
				if (previousCameraId && cameraId !== previousCameraId) this.stopRegisteredCameraStream()
			},
			value(isOpen, wasOpen){
				if (isOpen && !wasOpen) {
					this.resetTransientState()
					this.selectedListIds = this.normalizedInitialListIds()
					this.loadFaceLists()
				}
				if (!isOpen && wasOpen) this.reset()
			},
		},
		mounted(){
			if (this.value) {
				this.selectedListIds = this.normalizedInitialListIds()
				this.loadFaceLists()
			}
		},
		methods: {
			normalizedInitialListIds(){
				return Array.from(new Set((this.initialListIds || [])
					.map(value => value === undefined || value === null ? '' : String(value).trim())
					.filter(Boolean)))
			},
			handleDialogInput(isOpen){
				if (isOpen === false) {
					this.close()
					return
				}
				this.$emit('input', isOpen)
			},
			t(key, fallback){
				return this.$ml && this.$ml.get ? this.$ml.get(key) : fallback
			},
			isLocalhost(hostname){
				return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1'
			},
			isBrowserCameraSecureContext(location = (typeof window !== 'undefined' ? window.location : {}), isSecureContext = (typeof window !== 'undefined' ? window.isSecureContext : true)){
				return Boolean(isSecureContext || location.protocol === 'https:' || this.isLocalhost(location.hostname))
			},
			isBrowserCameraAvailable(){
				return this.isBrowserCameraSecureContext()
			},
			buildSources(browserCameraAvailable){
				const browserCameraHint = browserCameraAvailable ? '' : this.t('trained_face_capture_requires_secure_context', 'Camera access requires HTTPS or localhost. Open this page over HTTPS, use localhost during development, or upload an image instead.')
				return [
					{ key: 'upload', text: 'Upload', icon: 'mdi-upload', disabled: false },
					{ key: 'browser-camera', text: this.t('trained_face_capture_camera', 'Camera'), icon: 'mdi-camera', disabled: !browserCameraAvailable, hint: browserCameraHint },
					{ key: 'registered-camera', text: this.t('trained_face_capture_registered', 'Registered camera'), icon: 'mdi-cctv', disabled: false },
				]
			},
			async handleUploadFiles(files){
				const images = Array.from(files || []).filter(file => file && (!file.type || file.type.indexOf('image/') === 0))
				this.clearAttempts()
				if (!images.length) return

				this.loadingUpload = true

				try{
					await this.validateFiles(images, 'upload', { replace: true })
				}catch(error){
					this.messageErrorUpload = [formatFaceEnrollmentError(error, this.t)]
				}finally{
					this.loadingUpload = false
				}
			},
			async handleCapturedFile(file, source){
				if (!file) return
				try{
					await this.validateFiles([file], source || this.selectedSource, { replace: false })
				}catch(error){
					this.messageErrorUpload = [formatFaceEnrollmentError(error, this.t)]
				}
			},
			async validateFiles(files, source, { replace, preferLatest = false }){
				const nextAttempts = replace ? [] : [...this.attempts]
				const messages = replace ? [] : this.attempts.filter(attempt => !attempt.isValid).map(attempt => attempt.msg)
				const newAttempts = []
				for (let idx = 0; idx < files.length; idx++) {
					const file = files[idx]
					const imgBase64 = await this.$Helper.file_to_b64(file)
					const response = await FaceService.validateTrainingUpload([{ imgBase64 }])
					const validation = response && response.data && response.data.length ? response.data[0] : {}
					const sourceAttemptCount = nextAttempts.filter(attempt => attempt.source === source).length
					const attempt = this.buildAttempt({ file, imgBase64, validation, idx: sourceAttemptCount, source })
					nextAttempts.push(attempt)
					newAttempts.push(attempt)
					if (!attempt.isValid) messages.push(attempt.msg)
				}
				this.attempts = nextAttempts
				const preferredAttempt = preferLatest ? newAttempts.find(attempt => attempt.isValid) : null
				const firstValid = preferredAttempt || nextAttempts.find(attempt => attempt.isValid)
				this.selectedAttemptId = firstValid ? firstValid.id : (newAttempts[0] ? newAttempts[0].id : (nextAttempts[0] ? nextAttempts[0].id : null))
				this.messageErrorUpload = messages
			},
			buildAttempt({ file, imgBase64, validation, idx, source = 'upload' }){
				const isValid = Boolean(validation && validation.isValid)
				const isPending = Boolean(validation && validation.isPending)
				const isInvalid = !isValid && !isPending
				return {
					id: `${source}-${idx + 1}`,
					source,
					file,
					imgBase64,
					previewUrl: this.createPreviewUrl(file),
					isValid,
					isPending,
					isInvalid,
					accessTerminalOnly: validation && validation.accessTerminalOnly,
					observation: validation && validation.observation,
					msg: this.getFaceValidationMessage(validation),
				}
			},
			getFaceValidationMessage(item = {}){
				if (item.accessTerminalOnly) return item.observation || ACCESS_TERMINAL_ONLY_MESSAGE
				if (item.messageKey && this.$ml && this.$ml.get) return this.$ml.get(item.messageKey)
				return item.msg || (item.isValid ? 'face_photo_valid' : 'face_photo_invalid')
			},
			createPreviewUrl(file){
				if (typeof URL !== 'undefined' && URL.createObjectURL && file instanceof Blob) {
					return URL.createObjectURL(file)
				}
				return ''
			},
			revokePreviewUrl(url){
				if (url && typeof URL !== 'undefined' && URL.revokeObjectURL) {
					URL.revokeObjectURL(url)
				}
			},
			clearAttempts(){
				this.attempts.forEach(attempt => this.revokePreviewUrl(attempt.previewUrl))
				this.attempts = []
				this.selectedAttemptId = null
				this.messageErrorUpload = []
			},
			getCameraId(camera = {}){
				return camera.deviceId || camera.id || camera.host_id || camera.uuid || null
			},
			isActiveCamera(camera = {}){
				if (camera.active === false || camera.enabled === false || camera.status === 'offline') return false
				return true
			},
			pickDefaultRegisteredCamera(cameras = []){
				if (!cameras.length) return null
				const lastId = typeof localStorage !== 'undefined' ? localStorage.getItem('trained-face-registered-camera-id') : null
				return cameras.find(camera => this.getCameraId(camera) === lastId) ||
					cameras.find(camera => this.isActiveCamera(camera)) ||
					cameras[0]
			},
			normalizeRegisteredCameraResponse(response){
				const data = response && response.data ? response.data : response
				if (Array.isArray(data)) return data
				if (data && Array.isArray(data.rows)) return data.rows
				if (data && Array.isArray(data.data)) return data.data
				return []
			},
			normalizeListResponse(response){
				return normalizeServiceListResponse(response)
			},
			getListId(list = {}){
				return list.id || list.uuid || list._id || null
			},
			isFaceList(list = {}){
				const type = list.type || list.list_type || list.listType || list.category || list.ref || ''
				const normalizedType = String(type).toLowerCase().replace(/[^a-z0-9]/g, '')
				if (!normalizedType) return true
				if (normalizedType.indexOf('face') !== -1 || normalizedType.indexOf('facial') !== -1) return true
				return !['lpr', 'plate', 'licenseplate', 'vehicle', 'car', 'alarm', 'event', 'analytic'].some(blockedType => normalizedType.indexOf(blockedType) !== -1)
			},
			async loadFaceLists(){
				this.loadingFaceLists = true
				try{
					const response = await ListService.getListFor(1)
					this.faceLists = this.normalizeListResponse(response).filter(list => this.isFaceList(list))
				}catch(_error){
					this.faceLists = []
					this.listAssignmentError = this.t('trained_face_list_load_failed', 'Face lists could not be loaded. Try again or create a list here.')
				}finally{
					this.loadingFaceLists = false
				}
			},
			async createFaceListInline(){
				const name = String(this.newListName || '').trim()
				this.createListError = ''
				if (!name) {
					this.createListError = this.t('trained_face_list_name_required', 'List name is required')
					return false
				}
				this.creatingFaceList = true
				try{
					const response = await ListService.createList({ name, type: 'face' })
					const created = response && response.data ? response.data : response
					if (created) {
						this.faceLists = [...this.faceLists.filter(list => this.getListId(list) !== this.getListId(created)), created]
						const createdId = this.getListId(created)
						if (createdId) this.selectedListIds = [createdId]
					}
					this.newListName = ''
					this.listAssignmentError = ''
					return true
				}catch(error){
					this.createListError = error && error.response && error.response.data && error.response.data.message ? error.response.data.message : this.t('trained_face_list_create_failed', 'Face list could not be created. Check the name and try again.')
					return false
				}finally{
					this.creatingFaceList = false
				}
			},
			async loadRegisteredCameras(){
				this.loadingRegisteredCameras = true
				this.registeredCameraError = ''
				try{
					const response = await AccessControlService.listDevices()
					this.registeredCameras = this.normalizeRegisteredCameraResponse(response)
					const defaultCamera = this.pickDefaultRegisteredCamera(this.registeredCameras)
					this.selectedRegisteredCameraId = defaultCamera ? this.getCameraId(defaultCamera) : null
					if (!defaultCamera) this.registeredCameraError = 'No registered camera is available. Upload remains available as a fallback.'
				}catch(error){
					this.registeredCameras = []
					this.selectedRegisteredCameraId = null
					this.registeredCameraError = formatRegisteredCameraError(error, this.t, 'trained_face_capture_load_cameras_failed')
				}finally{
					this.loadingRegisteredCameras = false
				}
			},
			selectRegisteredCamera(cameraId){
				this.selectedRegisteredCameraId = cameraId
				if (cameraId && typeof localStorage !== 'undefined') localStorage.setItem('trained-face-registered-camera-id', cameraId)
			},
			getRegisteredStreamId(response = {}){
				const data = response && response.data ? response.data : response
				return data.streamSessionId || data.sessionId || data.id || null
			},
			getRegisteredStreamEventName(deviceId = this.selectedRegisteredCameraId){
				return deviceId ? `stream${deviceId}` : ''
			},
			async startRegisteredCameraPreview(){
				if (!this.selectedRegisteredCameraId || this.registeredStreamLoading) return false
				await this.stopRegisteredCameraStream()
				this.registeredStreamLoading = true
				this.registeredCameraError = ''
				try{
					const response = await FaceService.startRegisteredCameraStream(this.selectedRegisteredCameraId)
					const sessionId = this.getRegisteredStreamId(response)
					if (!sessionId) throw new Error(this.t('trained_face_capture_stream_unavailable', 'Registered camera stream could not start. Upload remains available as a fallback.'))
					this.registeredStreamSessionId = sessionId
					this.startRegisteredCameraHeartbeat(sessionId)
					this.subscribeRegisteredCameraStreamFrames(this.selectedRegisteredCameraId, sessionId)
					return true
				}catch(error){
					this.registeredStreamSessionId = null
					this.registeredStreamPreview = ''
					this.registeredCameraError = formatRegisteredCameraError(error, this.t, 'trained_face_capture_stream_unavailable')
					return false
				}finally{
					this.registeredStreamLoading = false
				}
			},
			startRegisteredCameraHeartbeat(sessionId){
				this.clearRegisteredCameraHeartbeat()
				this.sendRegisteredCameraHeartbeat(sessionId)
				this.registeredStreamHeartbeatTimer = setInterval(() => this.sendRegisteredCameraHeartbeat(sessionId), 20000)
			},
			clearRegisteredCameraHeartbeat(){
				if (this.registeredStreamHeartbeatTimer) clearInterval(this.registeredStreamHeartbeatTimer)
				this.registeredStreamHeartbeatTimer = null
			},
			startRegisteredCameraPreviewRefresh(sessionId){
				this.clearRegisteredCameraPreviewRefresh()
				this.refreshRegisteredCameraPreview(sessionId)
				this.registeredStreamPreviewTimer = setInterval(() => this.refreshRegisteredCameraPreview(sessionId), 5000)
			},
			clearRegisteredCameraPreviewRefresh(){
				if (this.registeredStreamPreviewTimer) clearInterval(this.registeredStreamPreviewTimer)
				this.registeredStreamPreviewTimer = null
			},
			subscribeRegisteredCameraStreamFrames(deviceId, sessionId){
				this.unsubscribeRegisteredCameraStreamFrames()
				const eventName = this.getRegisteredStreamEventName(deviceId)
				if (!eventName || !sessionId) return false
				const handler = frame => this.handleRegisteredCameraStreamFrame(frame)
				EventBus.$on(eventName, handler)
				this.registeredStreamEventName = eventName
				this.registeredStreamHandler = handler
				return true
			},
			unsubscribeRegisteredCameraStreamFrames(){
				if (this.registeredStreamEventName && this.registeredStreamHandler) {
					EventBus.$off(this.registeredStreamEventName, this.registeredStreamHandler)
				}
				this.registeredStreamEventName = ''
				this.registeredStreamHandler = null
			},
			handleRegisteredCameraStreamFrame(frame = {}){
				if (frame.source !== 'registered_camera') return false
				if (frame.sessionId !== this.registeredStreamSessionId) return false
				if (frame.deviceId !== this.selectedRegisteredCameraId) return false
				if (!frame.frameBase64) return false

				const contentType = frame.contentType || 'image/jpeg'
				this.registeredStreamLatestFrame = { ...frame, contentType }
				this.registeredStreamPreview = `data:${contentType};base64,${frame.frameBase64}`
				return true
			},
			async refreshRegisteredCameraPreview(sessionId = this.registeredStreamSessionId){
				if (!sessionId) return false
				try{
					const response = await FaceService.captureRegisteredCameraFrame(sessionId)
					const data = response && response.data ? response.data : response
					if (data && data.imgBase64) {
						this.registeredStreamPreview = `data:${data.contentType || 'image/jpeg'};base64,${data.imgBase64}`
					}
					return true
				}catch(_error){
					return false
				}
			},
			async sendRegisteredCameraHeartbeat(sessionId = this.registeredStreamSessionId){
				if (!sessionId) return false
				try{
					await FaceService.heartbeatRegisteredCameraStream(sessionId)
					return true
				}catch(error){
					this.registeredCameraError = formatRegisteredCameraError(error, this.t, 'trained_face_capture_stream_timeout')
					return false
				}
			},
			base64ToFile(imgBase64, contentType = 'image/jpeg', name = 'registered-camera-capture.jpg'){
				let blob
				try{
					const binary = atob(imgBase64)
					const bytes = new Uint8Array(binary.length)
					for (let idx = 0; idx < binary.length; idx++) bytes[idx] = binary.charCodeAt(idx)
					blob = new Blob([bytes], { type: contentType })
				}catch(_error){
					blob = new Blob([imgBase64], { type: contentType })
				}
				if (typeof File !== 'undefined') return new File([blob], name, { type: contentType })
				blob.name = name
				return blob
			},
			async captureRegisteredCameraSnapshot(){
				if (!this.registeredStreamSessionId) {
					this.registeredCameraError = this.t('trained_face_capture_not_ready', 'Camera preview is not ready')
					return false
				}
				this.loadingUpload = true
				this.registeredCameraError = ''
				try{
					let data = this.registeredStreamLatestFrame
					if (!data || !data.frameBase64) {
						const response = await FaceService.captureRegisteredCameraFrame(this.registeredStreamSessionId)
						data = response && response.data ? response.data : response
					}
					const imgBase64 = data && (data.imgBase64 || data.frameBase64)
					if (!imgBase64) throw new Error(this.t('trained_face_capture_snapshot_failed', 'Registered camera snapshot failed. Try again or upload an image instead.'))
					const file = this.base64ToFile(imgBase64, data.contentType || 'image/jpeg')
					await this.validateFiles([file], 'registered-camera', { replace: false, preferLatest: true })
					return true
				}catch(error){
					this.registeredCameraError = formatRegisteredCameraError(error, this.t, 'trained_face_capture_snapshot_failed')
					return false
				}finally{
					this.loadingUpload = false
				}
			},
			async stopRegisteredCameraStream(){
				const sessionId = this.registeredStreamSessionId
				this.clearRegisteredCameraHeartbeat()
				this.clearRegisteredCameraPreviewRefresh()
				this.unsubscribeRegisteredCameraStreamFrames()
				this.registeredStreamSessionId = null
				this.registeredStreamPreview = ''
				this.registeredStreamLatestFrame = null
				if (!sessionId) return false
				try{
					await FaceService.stopRegisteredCameraStream(sessionId)
					return true
				}catch(_error){
					return false
				}
			},
			buildFormData(){
				const formData = new FormData()
				Object.keys(this.info || {}).forEach(key => {
					if (this.info[key]) formData.append(key, this.info[key])
				})
				if (this.selectedAttempt && this.selectedAttempt.file) {
					formData.append('files0', this.selectedAttempt.file)
				}
				if (this.selectedAttempt && this.selectedAttempt.accessTerminalOnly) {
					formData.append('accessTerminalOnly', 'true')
					formData.append('faceValidationSource', 'access_terminal_fallback')
					formData.append('faceValidationObservation', this.selectedAttempt.observation || ACCESS_TERMINAL_ONLY_MESSAGE)
				}
				return formData
			},
			async saveEnrollment(){
				this.listAssignmentError = ''
				if (!this.selectedListIds.length) {
					this.listAssignmentError = this.t('trained_face_list_required', 'Choose at least one face list before saving.')
					return false
				}
				if (this.saveDisabled) return false
				if (!this.$store.getters['auth/permission'](this.$route.name || 'face', 'can_add', 'face')) {
					this.$EventBus.$emit('notifications_modal', { type: 'warning', message: this.$ml.get('no_have_permission_create') })
					return false
				}

				this.saving = true
				try{
					const response = await FaceService.saveTrainedPerson(this.buildFormData(), this.info.type)
					const savedFace = response && response.data ? response.data : {}
					const assignment = await FaceService.assignTrainedFaceToListsWithRetry(savedFace, this.selectedListIds, { retries: 2, delayMs: 250 })
					if (assignment && assignment.pending) {
						this.pendingListAssignment = { face: savedFace, listIds: [...this.selectedListIds], attempts: assignment.attempts }
						this.listAssignmentError = this.pendingListAssignmentMessage
						this.$emit('pending-list-assignment', this.pendingListAssignment)
						return false
					}
					this.$emit('saved', savedFace)
					this.reset()
					return true
				}catch(error){
					this.$EventBus.$emit('notifications_modal', {
						type: 'error',
						message: formatFaceEnrollmentError(error, this.t, 'trained_face_save_error_generic'),
					})
					return false
				}finally{
					this.saving = false
				}
			},
			async retryPendingListAssignment(){
				if (!this.pendingListAssignment) return false
				this.saving = true
				try{
					const { face, listIds } = this.pendingListAssignment
					const assignment = await FaceService.assignTrainedFaceToListsWithRetry(face, listIds, { retries: 2, delayMs: 250 })
					if (assignment && assignment.pending) {
						this.listAssignmentError = this.pendingListAssignmentMessage
						return false
					}
					this.pendingListAssignment = null
					this.listAssignmentError = ''
					this.$emit('saved', face)
					this.reset()
					return true
				}catch(_error){
					this.listAssignmentError = this.pendingListAssignmentMessage
					return false
				}finally{
					this.saving = false
				}
			},
			resetTransientState(){
				this.clearAttempts()
				this.selectedSource = 'upload'
				this.selectedRegisteredCameraId = null
				this.registeredCameraError = ''
				this.registeredStreamLoading = false
				this.registeredStreamPreview = ''
				this.registeredStreamLatestFrame = null
				this.selectedListIds = []
				this.newListName = ''
				this.listAssignmentError = ''
				this.createListError = ''
				this.pendingListAssignment = null
				this.info = { type: 'user', name: '', email: '' }
			},
			reset(){
				const stopPromise = this.stopRegisteredCameraStream()
				this.resetTransientState()
				return stopPromise
			},
			close(){
				const cleanup = this.reset()
				this.$emit('input', false)
				this.$emit('close', false)
				return cleanup
			},
		},
		beforeDestroy(){
			this.stopRegisteredCameraStream()
			this.clearAttempts()
		},
	}
</script>
